import * as Sentry from "@sentry/react";

import { normalizeApiPath } from "./normalize-api-path";

type Api = "omnibus" | "game-state";
type Outcome = "response" | "network_error" | "aborted";

/**
 * Share of API requests logged, from VITE_SENTRY_REQUEST_LOG_SAMPLE_RATE.
 * Unset or invalid means log everything. An unset GitHub variable arrives as
 * "", which Number() would read as 0, so blank is handled explicitly.
 */
export const parseRequestLogSampleRate = (value: string | undefined) => {
  if (value === undefined || value.trim() === "") {
    return 1;
  }
  const rate = Number(value);
  return Number.isFinite(rate) && rate >= 0 && rate <= 1 ? rate : 1;
};

const REQUEST_LOG_SAMPLE_RATE = parseRequestLogSampleRate(
  import.meta.env.VITE_SENTRY_REQUEST_LOG_SAMPLE_RATE,
);

// Fetch aborts reject with a DOMException, which isn't an Error subclass in
// every environment, so check the name rather than instanceof
const isAbortError = (error: unknown) =>
  typeof error === "object" &&
  error !== null &&
  "name" in error &&
  error.name === "AbortError";

/**
 * The Network Information API: Chromium only (including ChromeOS), and not in
 * TypeScript's DOM types. Values are the browser's estimates, rounded for
 * privacy: rtt to 25ms, downlink to 25kbps and capped at 10Mbps.
 */
interface NetworkInformation {
  effectiveType?: string;
  rtt?: number;
  downlink?: number;
}

/**
 * The user's connection quality when the request finished, so a slow request
 * can be told apart from a slow network. Empty in Safari and Firefox.
 */
const getConnectionAttributes = () => {
  const connection = (
    navigator as Navigator & { connection?: NetworkInformation }
  ).connection;

  return {
    connection_type: connection?.effectiveType,
    connection_rtt_ms: connection?.rtt,
    connection_downlink_mbps: connection?.downlink,
  };
};

type LogAttributes = Record<string, string | number | undefined>;

// Sentry sends an undefined attribute as an empty string, which would give
// numeric fields like status a second, string type
const withoutUndefined = (attributes: LogAttributes) =>
  Object.fromEntries(
    Object.entries(attributes).filter(([, value]) => value !== undefined),
  ) as Record<string, string | number>;

const logRequest = (attributes: {
  api: Api;
  request: Request;
  durationMs: number;
  outcome: Outcome;
  status?: number;
}) => {
  const { api, request, durationMs, outcome, status } = attributes;
  const failed =
    outcome === "network_error" || (status !== undefined && status >= 500);

  try {
    (failed ? Sentry.logger.warn : Sentry.logger.info)(
      "api_request",
      withoutUndefined({
        api,
        method: request.method,
        path: normalizeApiPath(request.url),
        status,
        duration_ms: Math.round(durationMs),
        outcome,
        page: normalizeApiPath(window.location.pathname),
        ...getConnectionAttributes(),
      }),
    );
  } catch {
    // Logging is best-effort and must never break the request
  }
};

/**
 * A fetch for the generated API clients that logs each request's duration to
 * Sentry Logs, so latency is visible for every request rather than only those
 * inside a sampled page-load trace. Duration is time to response headers;
 * body download isn't included.
 *
 * Calls globalThis.fetch per request so Sentry's fetch wrapper still applies.
 */
export const createLoggedFetch =
  (api: Api, sampleRate = REQUEST_LOG_SAMPLE_RATE) =>
  async (request: Request): Promise<Response> => {
    if (Math.random() >= sampleRate) {
      return globalThis.fetch(request);
    }

    const start = performance.now();
    try {
      const response = await globalThis.fetch(request);
      logRequest({
        api,
        request,
        durationMs: performance.now() - start,
        outcome: "response",
        status: response.status,
      });
      return response;
    } catch (error) {
      logRequest({
        api,
        request,
        durationMs: performance.now() - start,
        outcome: isAbortError(error) ? "aborted" : "network_error",
      });
      throw error;
    }
  };
